import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { InquiryService } from '../../../services/inquiry.service';
import { Inquiry } from '../../../models/inquiry.model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { HttpResponse } from '@angular/common/http';
import { ModalSnackbarService } from '../../../services/modal-snackbar.service';
import { LabelChipComponent } from '../../label-chip/label-chip.component';
import { FileUtilsService } from '../../../services/file-utils.service';
import { User, UserService } from '../../../services/user.service';

@Component({
  selector: 'app-inquiry-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MaterialModule,
    LabelChipComponent
  ],
  templateUrl: './inquiry-detail.component.html',
  styleUrls: ['./inquiry-detail.component.scss']
})
export class InquiryDetailComponent implements OnInit {
  inquiry!: Inquiry;
  form!: FormGroup;
  selectedFiles: File[] = [];
  currentUser!: User;
  visibleCompleteButton = false;

  constructor(
    private route: ActivatedRoute,
    private inquiryService: InquiryService,
    private fb: FormBuilder,
    private snackBar: ModalSnackbarService,
    private fileUtils: FileUtilsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    this.userService.getCurrentUser().subscribe(res => {
      this.currentUser = this.userService.parseData(res);
    });

    this.loadInquiry(id);

    this.form = this.fb.group({
      body: ['', [Validators.required, Validators.maxLength(1000)]],
      files: [[], this.fileUtils.fileSizeValidator(20 * 1024 * 1024)] // 20MB
    });
  }

  loadInquiry(id: number): void {
    this.inquiryService.get(id).subscribe({
      next: (res) => this.inquiry = res,
      error: () => this.snackBar.show('読み込み失敗')
    });
  }

  getUserName(id: number) {
    if (!this.currentUser) return '';
    if (this.currentUser.id !== id) return 'myfxpool サポート';

    return (this.currentUser.name) ? this.currentUser.name + ' 様' : this.currentUser.login + ' 様';
  }

  submit(): void {
    if (this.form.invalid) return;

    const formData = new FormData();
    formData.append('body', this.form.value.body);

    this.form.value.files.forEach((file: File, i: number) => {
      formData.append('attachments[]', file, file.name);
    });

    this.inquiryService.addChild(this.inquiry.id, formData).subscribe({
      next: () => {
        this.loadInquiry(this.inquiry.id);
        this.form.reset();
        this.selectedFiles = [];
        this.snackBar.show('返信を登録しました', '/inquiry');
      },
      error: () => {
        this.snackBar.show('送信に失敗しました');
      }
    });
  }

  markAsCompleted(): void {
    this.inquiryService.markAsCompleted(this.inquiry.id).subscribe({
      next: () => {
        this.snackBar.show('状態を完了に変更しました');
        this.loadInquiry(this.inquiry.id); // 最新状態を再読み込み
      },
      error: () => {
        this.snackBar.show('完了に変更できませんでした');
      }
    });
  }

  onDragOver(event: DragEvent): void {
    this.fileUtils.onDragOver(event);
  }

  onDrop(event: DragEvent): void {
    const newFiles = this.fileUtils.extractValidDroppedFiles(event, this.selectedFiles);
    if (!newFiles) {
      this.snackBar.show(`ファイルは最大 ${this.fileUtils.MAX_FILE_COUNT} 個までです`);
      return;
    }

    this.fileUtils.addFiles(this.selectedFiles, newFiles, files => {
      this.updateSelectedFiles(files);
    });
  }

  onFileChange(event: Event): void {
    const newFiles = this.fileUtils.extractValidSelectedFiles(event, this.selectedFiles);
    if (!newFiles) {
      this.snackBar.show(`ファイルは最大 ${this.fileUtils.MAX_FILE_COUNT} 個までです`);
      return;
    }

    this.fileUtils.addFiles(this.selectedFiles, newFiles, updated => {
      this.updateSelectedFiles(updated);
      const input = event.target as HTMLInputElement;
      if (input.files) {
        input.value = ''; // 同じファイルでも選び直し可能にする
      }
    });
  }

  removeFile(index: number): void {
    this.fileUtils.removeFile(this.selectedFiles, index, updated => {
      this.updateSelectedFiles(updated);
    });
  }

  downloadAttachment(attachmentId: number): void {
    this.inquiryService.downloadAttachment(attachmentId).subscribe({
      next: (res: HttpResponse<Blob>) => {
        this.fileUtils.downloadAttachment(res);
      },
      error: () => {
        this.snackBar.show('ダウンロードに失敗しました');
      }
    });
  }

  private updateSelectedFiles(files: File[]) {
    this.selectedFiles = files;
    this.form.get('files')?.setValue(files);
    this.form.get('files')?.updateValueAndValidity();
  }
}

